import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useCostTicket } from '@/hooks/use-analytics-v2';
import { formatCount, formatUsd } from '../cost-format';

interface CostTicketDialogProps {
  accountId: number;
  buId?: string | null;
  /** The ticket to show, or null for closed. */
  conversationId: number | null;
  onClose: () => void;
}

/** Per-response breakdown of one ticket: every LLM and API call, grouped by the response it was made for. */
export function CostTicketDialog({ accountId, buId, conversationId, onClose }: CostTicketDialogProps) {
  const { data, isLoading, isError } = useCostTicket(accountId, buId, conversationId);
  // The hook keeps the previous ticket while the next loads; never show it under the new title.
  const detail = data?.conversationId === conversationId ? data : undefined;

  return (
    <Dialog open={conversationId !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className='max-h-[85vh] overflow-y-auto sm:max-w-4xl'>
        <DialogHeader>
          <DialogTitle>Ticket #{conversationId}</DialogTitle>
          <DialogDescription className='tabular-nums'>
            {detail ? `Total ${formatUsd(detail.costUsd)}` : 'AI and API calls for this ticket'}
          </DialogDescription>
        </DialogHeader>

        {isLoading || (!detail && !isError) ? (
          <div className='flex flex-col gap-2'>
            <Skeleton className='h-8 w-full rounded-md' />
            <Skeleton className='h-8 w-full rounded-md' />
            <Skeleton className='h-8 w-full rounded-md' />
          </div>
        ) : isError || !detail ? (
          <p className='py-6 text-center text-sm text-grey-500'>Failed to load this ticket's usage.</p>
        ) : (
          <div className='flex flex-col gap-5'>
            {detail.responses.map((response) => (
              <section key={response.messageId ?? 'ticket-level'} className='flex flex-col gap-2'>
                <div className='flex items-baseline justify-between gap-3'>
                  <h3 className='text-[13px] font-medium text-[#2b2e34]'>
                    {response.messageId === null ? 'Ticket-level work' : `Response ${response.messageId}`}
                  </h3>
                  <span className='text-[13px] font-medium tabular-nums'>{formatUsd(response.costUsd)}</span>
                </div>
                <Table className='text-xs tabular-nums'>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Service</TableHead>
                      <TableHead>Feature</TableHead>
                      <TableHead>Model</TableHead>
                      <TableHead className='text-right'>Input</TableHead>
                      <TableHead className='text-right'>Cached</TableHead>
                      <TableHead className='text-right'>Output</TableHead>
                      <TableHead className='text-right'>Cost</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {response.events.map((event, i) => (
                      <TableRow key={`${event.occurredAt}-${i}`}>
                        <TableCell>{event.service}</TableCell>
                        <TableCell>{event.feature}</TableCell>
                        <TableCell>{event.model ?? (event.kind === 'api' ? `${event.provider} (API)` : '—')}</TableCell>
                        <TableCell className='text-right'>{formatCount(event.inputTokens)}</TableCell>
                        <TableCell className='text-right'>{formatCount(event.cachedInputTokens)}</TableCell>
                        <TableCell className='text-right'>{formatCount(event.outputTokens)}</TableCell>
                        <TableCell className='text-right'>
                          {event.costUsd !== null ? (
                            formatUsd(event.costUsd)
                          ) : event.kind === 'api' ? (
                            // Subscription vendors bill per plan, so a call has no price of its own.
                            <span className='text-grey-500'>No per-call price</span>
                          ) : (
                            <span className='text-grey-500'>Unpriced</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </section>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
